import { z } from "zod";

/**
 * Game notifications through the browser's Web Push (a home screen app on iPhone, any browser
 * on Android and computers). A browser that turns them on gives the api its push subscription
 * and the topics it wants; the game server sends to the player's subscriptions while they are
 * not in the game. The texts are written here in both languages, in the subscription's own.
 */
export const PUSH_TOPICS = ["daily", "war", "rift", "events", "friends", "market"] as const;
export const PushTopicSchema = z.enum(PUSH_TOPICS);
export type PushTopic = z.infer<typeof PushTopicSchema>;

export const PushLangSchema = z.enum(["en", "tr"]);
export type PushLang = z.infer<typeof PushLangSchema>;

/**
 * The iOS app's device token, written as an endpoint: `apns://production/<hex token>` (TestFlight
 * and the App Store) or `apns://sandbox/<hex token>` (a debug build).
 */
export const APNS_ENDPOINT = /^apns:\/\/(production|sandbox)\/([0-9a-f]{64,200})$/;

/** A browser's push service address, or the iOS app's device token. */
export const PushEndpointSchema = z.union([
  z.string().url().max(1000).startsWith("https://"),
  z.string().regex(APNS_ENDPOINT),
]);

/**
 * What the browser's PushManager gives (PushSubscription.toJSON()), read strictly; the iOS app
 * sends its device token the same way, with "apns" for both keys (Apple needs none).
 */
export const PushSubscriptionSchema = z.union([
  z.object({
    endpoint: z.string().url().max(1000).startsWith("https://"),
    keys: z.object({
      p256dh: z.string().min(1).max(200),
      auth: z.string().min(1).max(100),
    }),
  }),
  z.object({
    endpoint: z.string().regex(APNS_ENDPOINT),
    keys: z.object({ p256dh: z.literal("apns"), auth: z.literal("apns") }),
  }),
]);

/** POST /account/push: turns notifications on (or changes the topics) for this browser. */
export const PushSubscribeSchema = z.strictObject({
  subscription: PushSubscriptionSchema,
  lang: PushLangSchema,
  topics: z.array(PushTopicSchema).max(PUSH_TOPICS.length),
});
export type PushSubscribe = z.infer<typeof PushSubscribeSchema>;

/** POST /account/push/remove: this browser's notifications off. */
export const PushRemoveSchema = z.strictObject({ endpoint: PushEndpointSchema });

/** One notification: what the service worker shows (public/sw.js). */
export interface PushPayload {
  title: string;
  body: string;
  /** Opened on a tap. */
  url: string;
  /** A newer notification with the same tag replaces the older one. */
  tag: string;
}

/** A notification before it is written in a language. */
export type PushNote =
  | { kind: "daily"; day: number; days: number }
  | { kind: "war"; zone: string; minutes: number; level: number }
  | { kind: "rift"; minutes: number; level: number }
  | { kind: "event"; event: "xp" | "drop" | "gold"; pct: number; minutes: number }
  | { kind: "friend"; name: string }
  | { kind: "market"; items: number; gold: number };

const TOPIC: Record<PushNote["kind"], PushTopic> = {
  daily: "daily",
  war: "war",
  rift: "rift",
  event: "events",
  friend: "friends",
  market: "market",
};
export const pushTopic = (n: PushNote): PushTopic => TOPIC[n.kind];

const EVENT_NAME: Record<PushLang, Record<"xp" | "drop" | "gold", string>> = {
  en: { xp: "XP", drop: "Drop rate", gold: "Gold" },
  tr: { xp: "XP", drop: "Drop", gold: "Altın" },
};

const gold = (n: number, lang: PushLang) => n.toLocaleString(lang === "tr" ? "tr-TR" : "en-US");

/** The notification's text in a language. `site` is the website's address (no trailing /). */
export function pushPayload(n: PushNote, lang: PushLang, site: string): PushPayload {
  const tr = lang === "tr";
  const play = `${site}/play/`;
  const title = "WarCrest Online";
  switch (n.kind) {
    case "daily":
      return {
        title,
        body: tr
          ? `Günlük ödülün seni bekliyor: ${n.days} günün ${n.day}. günü.`
          : `Your daily reward is waiting: day ${n.day} of ${n.days}.`,
        url: play,
        tag: "daily",
      };
    case "war":
      return {
        title,
        body: tr
          ? `${n.zone} savaşı ${n.minutes} dakika içinde başlıyor! Seviye ${n.level}+ oyuncular katılabilir.`
          : `The war of ${n.zone} begins in ${n.minutes} minutes! Level ${n.level}+ may join.`,
        url: play,
        tag: "war",
      };
    case "rift":
      return {
        title,
        body: tr
          ? `Bowl'daki Rift Kapısı ${n.minutes} dakika boyunca açık! Seviye ${n.level}+ oyuncular girebilir.`
          : `The Rift Gate in the Bowl is open for ${n.minutes} minutes! Level ${n.level}+ may enter.`,
        url: play,
        tag: "rift",
      };
    case "event":
      return {
        title,
        body: tr
          ? `${EVENT_NAME.tr[n.event]} etkinliği: ${n.minutes} dakika boyunca +%${n.pct}!`
          : `${EVENT_NAME.en[n.event]} event: +${n.pct}% for ${n.minutes} minutes!`,
        url: play,
        tag: `event-${n.event}`,
      };
    case "friend":
      return {
        title,
        body: tr ? `${n.name} oyunda.` : `${n.name} is in the game.`,
        url: play,
        tag: `friend-${n.name}`,
      };
    case "market":
      return {
        title,
        body: tr
          ? `Tezgâhın ${n.items} eşya sattı: ${gold(n.gold, lang)} altın seni bekliyor.`
          : `Your stall sold ${n.items} item${n.items === 1 ? "" : "s"}: ${gold(n.gold, lang)} gold is waiting for you.`,
        url: play,
        tag: "market",
      };
  }
}
